import env from '@config/env';
import MerchantLoginPage from '@modules/merchant/authentication/pages/LoginPage';
import BusinessDetailPage from '@modules/merchant/onboarding/pages/BusinessDetailPage';
import CreateProfilePage from '@modules/merchant/onboarding/pages/CreateProfilePage';
import RequestSubmittedPage from '@modules/merchant/onboarding/pages/RequestSubmittedPage';
import SearchBusinessPage from '@modules/merchant/onboarding/pages/SearchBusinessPage';
import VerifyOtpPage from '@modules/merchant/onboarding/pages/VerifyOtpPage';
import lazy from '@utils/lazyWithRetry';

const layoutMap = {
  admin: lazy(() => import('@layouts/AdminLayout')),
  businessuser: lazy(() => import('@layouts/BusinessUserLayout')),
  merchant: lazy(() => import('@layouts/MerchantLayout')),
};

const merchantOnboardingPages = {
  LoginPage: MerchantLoginPage,
  VerifyOtpPage,
  CreateProfilePage,
  SearchBusinessPage,
  BusinessDetailPage,
  RequestSubmittedPage,
};

const lazyOnboardingPages = {
  VerifyOtpPage: lazy(() => import('@modules/merchant/onboarding/pages/VerifyOtpPage')),
  CreateProfilePage: lazy(() => import('@modules/merchant/onboarding/pages/CreateProfilePage')),
  SearchBusinessPage: lazy(() => import('@modules/merchant/onboarding/pages/SearchBusinessPage')),
  BusinessDetailPage: lazy(() => import('@modules/merchant/onboarding/pages/BusinessDetailPage')),
  RequestSubmittedPage: lazy(
    () => import('@modules/merchant/onboarding/pages/RequestSubmittedPage'),
  ),
};

const pageMap = {
  admin: {
    LoginPage: lazy(() => import('@modules/admin/authentication/pages/LoginPage')),
    ResetPasswordPage: lazy(() => import('@modules/admin/authentication/pages/ResetPasswordPage')),
    DashboardPage: lazy(() => import('@modules/admin/dashboard/pages/DashboardPage')),
    ProfilePage: lazy(() => import('@modules/admin/profile/pages/ProfilePage')),
    SettingsPage: lazy(() => import('@modules/admin/settings/pages/SettingsPage')),
    UsersPage: lazy(() => import('@modules/admin/users/pages/UsersPage')),
    AddAdminPage: lazy(() => import('@modules/admin/users/pages/AddAdminPage')),
    SavvyNudgePage: lazy(() => import('@modules/admin/nudges/pages/SavvyNudgePage')),
    QuestsPage: lazy(() => import('@modules/admin/quests/pages/QuestsPage')),
    CreateQuestPage: lazy(() => import('@modules/admin/quests/pages/CreateQuestPage')),
    ManageQuestPage: lazy(() => import('@modules/admin/quests/pages/ManageQuestPage')),
    BusinessUsersPage: lazy(() => import('@modules/admin/users/pages/BusinessUsersPage')),
    AddBusinessUserPage: lazy(() => import('@modules/admin/users/pages/AddBusinessUserPage')),
    MerchantUsersPage: lazy(() => import('@modules/admin/users/pages/MerchantUsersPage')),
    MerchantUserDetailPage: lazy(() => import('@modules/admin/users/pages/MerchantUserDetailPage')),
    MerchantUserEditProfilePage: lazy(
      () => import('@modules/admin/users/pages/MerchantUserEditProfilePage'),
    ),
    ConsumersPage: lazy(() => import('@modules/admin/users/pages/ConsumersPage')),
    ConsumerDetailPage: lazy(() => import('@modules/admin/users/pages/ConsumerDetailPage')),
    MerchantBusinessesPage: lazy(
      () => import('@modules/admin/businesses/pages/MerchantBusinessesPage'),
    ),
    MerchantBusinessDetailPage: lazy(
      () => import('@modules/admin/businesses/pages/MerchantBusinessDetailPage'),
    ),
    MerchantBusinessProfilePage: lazy(
      () => import('@modules/admin/businesses/pages/MerchantBusinessProfilePage'),
    ),
    BrandBusinessesPage: lazy(() => import('@modules/admin/businesses/pages/BrandBusinessesPage')),
    VersionManagementPage: lazy(
      () => import('@modules/admin/settings/pages/VersionManagementPage'),
    ),
    UpdateVersionBuildPage: lazy(
      () => import('@modules/admin/settings/pages/UpdateVersionBuildPage'),
    ),
    BusinessQueriesPage: lazy(() => import('@modules/admin/queries/pages/BusinessQueriesPage')),
    BusinessQueryDetailPage: lazy(
      () => import('@modules/admin/queries/pages/BusinessQueryDetailPage'),
    ),
    ResourcesPage: lazy(() => import('@modules/admin/resources/pages/ResourcesPage')),
    IconManagementPage: lazy(() => import('@modules/admin/app-icons/pages/IconManagementPage')),
    IconFormPage: lazy(() => import('@modules/admin/app-icons/pages/IconFormPage')),
    CategoriesPage: lazy(() => import('@modules/admin/categories/pages/CategoriesPage')),
    CategoryFormPage: lazy(() => import('@modules/admin/categories/pages/CategoryFormPage')),
    CategorySectionsPage: lazy(
      () => import('@modules/admin/categories/pages/CategorySectionsPage'),
    ),
    CategorySectionFormPage: lazy(
      () => import('@modules/admin/categories/pages/CategorySectionFormPage'),
    ),
    CategorySubsectionsPage: lazy(
      () => import('@modules/admin/categories/pages/CategorySubsectionsPage'),
    ),
    CategorySubsectionFormPage: lazy(
      () => import('@modules/admin/categories/pages/CategorySubsectionFormPage'),
    ),
    CreateNudgePage: lazy(() => import('@modules/admin/businesses/pages/AdminCreateNudgePage')),
    ManageNudgePage: lazy(() => import('@modules/admin/businesses/pages/AdminManageNudgePage')),
    TeamMemberCreatePage: lazy(
      () => import('@modules/admin/businesses/pages/AdminTeamMemberCreatePage'),
    ),
    TeamMemberAssignPage: lazy(
      () => import('@modules/admin/businesses/pages/AdminTeamMemberAssignPage'),
    ),
    TeamMemberManagePage: lazy(
      () => import('@modules/admin/businesses/pages/AdminTeamMemberManagePage'),
    ),
    FeedFollowerDetailPage: lazy(
      () => import('@modules/merchant/feed/pages/FeedFollowerDetailPage'),
    ),
  },
  businessuser: {
    LoginPage: lazy(() => import('@modules/businessuser/authentication/pages/LoginPage')),
    DashboardPage: lazy(() => import('@modules/businessuser/dashboard/pages/DashboardPage')),
    ProfilePage: lazy(() => import('@modules/businessuser/profile/pages/ProfilePage')),
    SettingsPage: lazy(() => import('@modules/businessuser/settings/pages/SettingsPage')),
    UsersPage: lazy(() => import('@modules/businessuser/users/pages/UsersPage')),
    ...lazyOnboardingPages,
  },
  merchant: {
    ...merchantOnboardingPages,
    DashboardPage: lazy(() => import('@modules/merchant/dashboard/pages/DashboardPage')),
    SetupGuidePage: lazy(() => import('@modules/merchant/dashboard/pages/TutorialGuidePage')),
    GalleryPage: lazy(() => import('@modules/merchant/gallery/pages/GalleryPage')),
    FeedPage: lazy(() => import('@modules/merchant/feed/pages/FeedPage')),
    FeedFollowerDetailPage: lazy(
      () => import('@modules/merchant/feed/pages/FeedFollowerDetailPage'),
    ),
    TeamPage: lazy(() => import('@modules/merchant/team/pages/TeamPage')),
    TeamMemberCreatePage: lazy(() => import('@modules/merchant/team/pages/TeamMemberCreatePage')),
    TeamMemberAssignPage: lazy(() => import('@modules/merchant/team/pages/TeamMemberAssignPage')),
    TeamMemberManagePage: lazy(() => import('@modules/merchant/team/pages/TeamMemberManagePage')),
    NudgesPage: lazy(() => import('@modules/merchant/nudges/pages/NudgesPage')),
    CreateNudgePage: lazy(() => import('@modules/merchant/nudges/pages/CreateNudgePage')),
    ManageNudgePage: lazy(() => import('@modules/merchant/nudges/pages/ManageNudgePage')),
    ManageBusinessPage: lazy(
      () => import('@modules/merchant/manage-business/pages/ManageBusinessPage'),
    ),
    BillingReturnPage: lazy(
      () => import('@modules/merchant/manage-business/pages/BillingReturnPage'),
    ),
    ProfilePage: lazy(() => import('@modules/merchant/profile/pages/ProfilePage')),
    SettingsPage: lazy(() => import('@modules/merchant/settings/pages/SettingsPage')),
    UsersPage: lazy(() => import('@modules/merchant/users/pages/UsersPage')),
    ResourcesPage: lazy(() => import('@modules/merchant/resources/pages/ResourcesPage')),
    ToolsPage: lazy(() => import('@modules/merchant/tools/pages/ToolsPage')),
  },
};

const portalType = env.APP_TYPE;
const NotFoundPage = lazy(() => import('@routes/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('@routes/UnauthorizedPage'));

export const PortalLayout = layoutMap[portalType] || layoutMap.admin;
export const routes = {
  ...(pageMap[portalType] || pageMap.admin),
  NotFoundPage,
  UnauthorizedPage,
};

export default routes;
